import {RoundedCalculations} from "@client/calculations";
import {DragHandler} from "@client/components/DragHandler";
import {EntryIcon} from "@client/components/EntryIcon";
import {TouchHandler} from "@client/components/TouchHandler";
import * as dialogs from "@client/features/dialogs";
import {Checkbox, SelectText} from "@client/forms";
import * as forms from "@client/forms";
import {ConversionInput} from "@client/forms/widgets";
import type {Locator} from "@client/gotterdammerung";
import {formatUnit} from "@client/utils";
import {DraggableProvidedDragHandleProps} from "@hello-pangea/dnd";
import {PhotoIcon} from "@heroicons/react/24/outline";
import {classNames} from "@reactivated";
import {AnimatePresence, motion} from "motion/react";
import * as React from "react";

const styles = {
    card: "card-entry flex gap-[5px] border-b border-[#D8D8D8] text-xs py-[15px] px-[11px] pl-[10px]",
    name: "font-medium text-sm text-[#373A3C]",
    indented: "pl-6",
    description: "flex-1 whitespace-nowrap overflow-hidden text-ellipsis",
    quantity: "card-quantity w-[30%]",
    primaryMetrics: "text-right",
    calories: "font-semibold text-[10px] text-[#373A3C] text-right",
    quantityAndUnit: "font-normal text-[10px] text-[#373A3C] text-right mt-[5px]",
    macros: "card-macros mt-[5px] font-normal text-[11px] text-[#373A3C] flex items-baseline",
    hiddenMacros: "hidden",
    isDragging:
        "bg-[repeating-linear-gradient(45deg,#FFFFFF,#FFFFFF_10px,#EEEEEE_10px,#EEEEEE_20px)]",
    isComplete: "",
    tag: "bg-[#E2F4FB] text-[#181a1b] font-medium leading-none mr-[5px] py-[3px] px-[7px] rounded-[5px] lowercase text-[11px] block",
} as const;

interface Props {
    sorting?: {
        handle: DraggableProvidedDragHandleProps | null;
        isDragging: boolean;
    };
    largeTitle?: boolean;
    isProcessing?: "audio" | "image";

    extra?: React.ReactNode;
    photo?: {
        image_uid: string;
    };
    icon?: {cloudflare_image_id: string | null} | null;
    link?: string | Locator | (() => void);
    item: {
        id?: number;
        uuid?: string;
        name: string;
    };
    macros: RoundedCalculations;

    indented?: boolean;

    quantityAndUnitHandler?: {
        quantity: {
            multiplier?: DecimalValue | null;
            value: DecimalValue | null;
            onChange: (value: DecimalValue | null) => void;
        };
        unit: {
            value: string;
            options: string[];
            onChange: (value: string) => void;
        };
    };

    removeHandler?: {
        onRemove: () => void;
        isRemoved: boolean;
    };

    quantityAndUnitDisplay?: {
        quantity: string;
        unit: string;
    };

    switchHandler?: {
        value: boolean | null;
        onSwitch: () => void;
    };

    hiddenMacros: boolean;
    tag?: string;
}

interface UnitQuantityWidgetProps {
    quantity: {
        multiplier?: DecimalValue | null;
        value: DecimalValue | null;
        onChange: (value: DecimalValue | null) => void;
    };
    unit: {
        value: string;
        options: string[];
        onChange: (value: string) => void;
    };
    isRemoved?: boolean;
}

export const UnitQuantityWidget = ({
    quantity,
    unit,
    isRemoved,
}: UnitQuantityWidgetProps) => {
    return (
        <div className="flex -space-x-px">
            <div
                className="w-10 sm:w-16"
                onFocus={forms.handleInputFocusSelectAndPreventContextMenu}
            >
                <ConversionInput
                    key={`${unit.value}-${isRemoved}`}
                    unit={{
                        condition: true,
                        multiplier: quantity.multiplier ?? "1",
                        suffixes: ["", ""],
                    }}
                    name={"quantity"}
                    ariaLabel="quantity"
                    className={classNames(
                        "text-sm block w-full min-w-0 flex-1 rounded-none rounded-l-md border-0 py-1.5 px-2 sm:px-3 text-gray-900 ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600",
                        "py-1 focus:z-10 text-xs sm:text-sm",
                    )}
                    onChange={quantity.onChange}
                    value={isRemoved == true ? null : quantity.value}
                />
            </div>
            <div className="w-16 sm:w-24">
                <SelectText
                    name="unit"
                    ariaLabel="unit"
                    className={classNames(
                        `relative block w-full appearance-none truncate rounded-r-md border-0 bg-transparent bg-none py-1.5 pr-2 pl-2 text-xs text-gray-900 ring-1 ring-gray-300 ring-inset focus:z-10 focus:ring-2 focus:ring-indigo-600 focus:ring-inset sm:text-sm`,
                    )}
                    onChange={unit.onChange}
                    value={unit.value}
                >
                    {unit.options.map((unit) => (
                        <option key={unit} value={unit}>
                            {formatUnit(unit)}
                        </option>
                    ))}
                </SelectText>
            </div>
        </div>
    );
};

export const Card = ({sorting, item, link, macros, hiddenMacros, ...props}: Props) => {
    const useKilojoules = forms.useUseKilojoules();
    const photoDialog = dialogs.useDialog<unknown, {photo: {image_uid: string}}>({
        Content: (props) => {
            return (
                <img
                    alt={item.name}
                    src={`https://imagedelivery.net/ELiLiOfYeVi79Put2yGLDg/${props.photo.image_uid}/public`}
                />
            );
        },
    });

    const quantityAndUnit =
        props.quantityAndUnitDisplay != null
            ? `${props.quantityAndUnitDisplay.quantity} ${formatUnit(
                  props.quantityAndUnitDisplay.unit,
              )}`
            : null;
    const photo = props.photo;

    const content =
        props.isProcessing != null ? (
            <motion.div
                key="processing"
                initial={{opacity: 0}}
                animate={{opacity: 1}}
                exit={{opacity: 0}}
                className={classNames(
                    styles.description,
                    "flex items-center min-h-[42px] text-base",
                )}
            >
                {props.isProcessing == "audio"
                    ? "Audio saved. Processing..."
                    : "Image saved. Processing..."}
            </motion.div>
        ) : (
            <motion.div
                key="content"
                className={styles.description}
                initial={{opacity: 0}}
                animate={{opacity: 1, scale: [1, 1.07, 1]}}
                exit={{opacity: 0}}
            >
                <span
                    data-id={item.id}
                    className={classNames(
                        styles.name,
                        props.largeTitle == true && "text-lg! font-normal!",
                    )}
                >
                    {item.name}
                </span>

                <div
                    className={classNames(
                        "@container",
                        styles.macros,
                        hiddenMacros && styles.hiddenMacros,
                        "text-xs!",
                    )}
                >
                    {props.tag != null && <div className={styles.tag}>{props.tag}</div>}
                    <span>
                        {macros.protein}g{" "}
                        <span>
                            p<span className="hidden @[220px]:inline">rotein</span>
                        </span>
                    </span>

                    <span>
                        {macros.fat}g{" "}
                        <span>
                            f<span className="hidden @[220px]:inline">at</span>
                        </span>
                    </span>

                    <span>
                        {macros.carbs}g{" "}
                        <span>
                            c<span className="hidden @[220px]:inline">arbs</span>
                        </span>
                    </span>
                    {macros.fiber != null && (
                        <span className={"hide-on-mobile"}>{macros.fiber}g fiber</span>
                    )}
                    <span className={"hide-on-mobile"}>
                        {macros.net_carbs}g net carbs
                    </span>
                </div>
            </motion.div>
        );

    const touchHandler = (
        <TouchHandler
            link={props.isProcessing != null ? undefined : link}
            onClick={
                props.isProcessing != null ? undefined : props.switchHandler?.onSwitch
            }
            className={classNames(
                "card", // Selenium
                styles.card,
                props.indented === true && styles.indented,
                sorting?.isDragging === true && styles.isDragging,
                "items-center",
            )}
        >
            {sorting != null && <DragHandler handle={sorting.handle} />}
            <div className="flex flex-col gap-1" id={item.uuid}>
                {props.quantityAndUnitHandler != null && (
                    <div
                        className={classNames(
                            props.removeHandler?.isRemoved == true && "opacity-30",
                        )}
                    >
                        <UnitQuantityWidget
                            quantity={props.quantityAndUnitHandler.quantity}
                            unit={props.quantityAndUnitHandler.unit}
                            isRemoved={props.removeHandler?.isRemoved}
                        />
                    </div>
                )}
                {props.extra}
            </div>

            {/* Because the whole row is clickable the checkbox should not be */}
            {props.switchHandler != null && (
                <Checkbox
                    name=""
                    value={props.switchHandler.value}
                    onChange={() => {}}
                />
            )}
            <EntryIcon icon={props.icon} size="card" />
            <AnimatePresence mode="popLayout" initial={false}>
                {content}
            </AnimatePresence>
            <AnimatePresence initial={false}>
                {props.isProcessing == null && (
                    <motion.div
                        className={classNames(
                            styles.primaryMetrics,
                            "flex items-center",
                        )}
                        initial={{opacity: 0}}
                        animate={{opacity: 1}}
                        exit={{opacity: 0}}
                    >
                        {photo != null && (
                            <div className="mr-3 flex justify-center">
                                <button
                                    type="button"
                                    className="size-5"
                                    onClick={(event) => {
                                        event.preventDefault();
                                        event.stopPropagation();
                                        void photoDialog({photo});
                                    }}
                                >
                                    <PhotoIcon className="size-5" />
                                </button>
                            </div>
                        )}
                        <div>
                            {hiddenMacros === false ? (
                                <>
                                    <span className={styles.calories}>
                                        {macros.calories} {useKilojoules ? "kj" : "cal"}
                                    </span>

                                    <div className={styles.quantityAndUnit}>
                                        {quantityAndUnit}
                                    </div>
                                </>
                            ) : (
                                <span className={styles.calories}>
                                    {quantityAndUnit}
                                </span>
                            )}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
            {props.removeHandler != null && (
                <button
                    type="button"
                    onClick={props.removeHandler.onRemove}
                    className={classNames(
                        props.removeHandler.isRemoved
                            ? "text-green-700 hover:text-green-900"
                            : "text-red-700 hover:text-red-900",
                    )}
                >
                    {props.removeHandler.isRemoved ? (
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 24 24"
                            fill="currentColor"
                            className="size-6"
                        >
                            <path
                                fillRule="evenodd"
                                d="M12 5.25c1.213 0 2.415.046 3.605.135a3.256 3.256 0 0 1 3.01 3.01c.044.583.077 1.17.1 1.759L17.03 8.47a.75.75 0 1 0-1.06 1.06l3 3a.75.75 0 0 0 1.06 0l3-3a.75.75 0 0 0-1.06-1.06l-1.752 1.751c-.023-.65-.06-1.296-.108-1.939a4.756 4.756 0 0 0-4.392-4.392 49.422 49.422 0 0 0-7.436 0A4.756 4.756 0 0 0 3.89 8.282c-.017.224-.033.447-.046.672a.75.75 0 1 0 1.497.092c.013-.217.028-.434.044-.651a3.256 3.256 0 0 1 3.01-3.01c1.19-.09 2.392-.135 3.605-.135Zm-6.97 6.22a.75.75 0 0 0-1.06 0l-3 3a.75.75 0 1 0 1.06 1.06l1.752-1.751c.023.65.06 1.296.108 1.939a4.756 4.756 0 0 0 4.392 4.392 49.413 49.413 0 0 0 7.436 0 4.756 4.756 0 0 0 4.392-4.392c.017-.223.032-.447.046-.672a.75.75 0 0 0-1.497-.092c-.013.217-.028.434-.044.651a3.256 3.256 0 0 1-3.01 3.01 47.953 47.953 0 0 1-7.21 0 3.256 3.256 0 0 1-3.01-3.01 47.759 47.759 0 0 1-.1-1.759L6.97 15.53a.75.75 0 0 0 1.06-1.06l-3-3Z"
                                clipRule="evenodd"
                            />
                        </svg>
                    ) : (
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 24 24"
                            fill="currentColor"
                            className="size-6"
                        >
                            <path
                                fillRule="evenodd"
                                d="M12 2.25c-5.385 0-9.75 4.365-9.75 9.75s4.365 9.75 9.75 9.75 9.75-4.365 9.75-9.75S17.385 2.25 12 2.25Zm-1.72 6.97a.75.75 0 1 0-1.06 1.06L10.94 12l-1.72 1.72a.75.75 0 1 0 1.06 1.06L12 13.06l1.72 1.72a.75.75 0 1 0 1.06-1.06L13.06 12l1.72-1.72a.75.75 0 1 0-1.06-1.06L12 10.94l-1.72-1.72Z"
                                clipRule="evenodd"
                            />
                        </svg>
                    )}
                </button>
            )}
        </TouchHandler>
    );
    return (
        <>
            {photoDialog.dialog}
            {touchHandler}
        </>
    );
};
